import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { getFieldMeta } from "../api/FlaskAPI";
import { endSession } from "../api/session";
import { setAreaUnit } from "../reducers/SettingsSlice";
import { setCurrentField } from "../reducers/MapSlice";
import { AREA_UNITS } from "../utils/areaUnits";
import { LANGUAGES } from "../i18n";

// The account button and its menu (area unit, language, logout).
export default function AccountMenu({ variant = "avatar" }) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const user = useSelector((state) => state.auth?.user);
  const areaUnit = useSelector((state) => state.settings.areaUnit);
  const currentField = useSelector((state) => state.mapState.currentField);
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);

  const handleAreaUnitChange = async (unit) => {
    dispatch(setAreaUnit(unit));
    // The open field holds its area in the old unit; reload it in the new one.
    if (currentField?.id) {
      try {
        const response = await getFieldMeta(currentField.id, unit);
        dispatch(setCurrentField(response.data));
      } catch (error) {
        console.error("Error reloading field in new area unit:", error);
      }
    }
  };

  const displayName = user?.full_name || user?.username || "";
  const initials =
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join("") || "?";

  const avatar = (
    <Avatar
      sx={{
        width: 30,
        height: 30,
        fontSize: "0.8rem",
        fontWeight: 600,
        bgcolor: "primary.main",
      }}
    >
      {initials}
    </Avatar>
  );
  const open = (e) => setAnchorEl(e.currentTarget);

  const choiceList = (key, title, options, current, onPick) => [
    <Typography
      key={key}
      sx={{
        px: 2,
        pt: 1,
        pb: 0.5,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "text.secondary",
      }}
    >
      {title}
    </Typography>,
    ...options.map((option) => (
      <MenuItem
        key={`${key}-${option.value}`}
        selected={option.value === current}
        onClick={() => onPick(option.value)}
        sx={{
          fontSize: 14,
          py: 0.75,
          // tint keeps the ✓ visible.
          "&.Mui-selected, &.Mui-selected:hover": {
            bgcolor: "rgba(var(--primary-rgb), 0.08)",
            color: "primary.main",
            fontWeight: 600,
          },
        }}
      >
        <ListItemIcon>
          {option.value === current && (
            <CheckOutlinedIcon fontSize="small" color="primary" />
          )}
        </ListItemIcon>
        {option.label}
      </MenuItem>
    )),
  ];
  const row = variant === "row";

  return (
    <>
      {row ? (
        <ButtonBase
          onClick={open}
          aria-label={t("account.button")}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            p: 1,
            borderRadius: "8px",
            textAlign: "left",
            "&:hover": { bgcolor: "#f3f4f6" },
          }}
        >
          {avatar}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>
              {user?.full_name || user?.username}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, color: "text.secondary" }}>
              {user?.username}
            </Typography>
          </Box>
          <MoreHorizIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        </ButtonBase>
      ) : (
        <Tooltip title={t("account.button")}>
          <IconButton
            onClick={open}
            size="small"
            aria-label={t("account.button")}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
            sx={{ p: "2px" }}
          >
            {avatar}
          </IconButton>
        </Tooltip>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        // sidebar's bottom row, open upwards.
        anchorOrigin={{
          vertical: row ? "top" : "bottom",
          horizontal: row ? "left" : "right",
        }}
        transformOrigin={{
          vertical: row ? "bottom" : "top",
          horizontal: row ? "left" : "right",
        }}
        PaperProps={{
          sx: {
            // as wide as the account row.
            width: row ? anchorEl?.offsetWidth : undefined,
            minWidth: 232,
            mt: row ? -1 : 1,
            borderRadius: "12px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2,
            py: 1,
          }}
        >
          {avatar}
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>
              {user?.full_name || "N/A"}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, color: "text.secondary" }}>
              {user?.username || "N/A"}
            </Typography>
          </Box>
        </Box>
        <Divider />
        {choiceList(
          "unit",
          t("account.areaUnit"),
          AREA_UNITS.map((unit) => ({
            value: unit.value,
            label: t(`units.${unit.value}.label`),
          })),
          areaUnit,
          handleAreaUnitChange,
        )}
        <Divider />
        {choiceList(
          "language",
          t("account.language"),
          LANGUAGES,
          i18n.resolvedLanguage,
          (language) => i18n.changeLanguage(language),
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setConfirmLogoutOpen(true);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "inherit" }}>
            <LogoutOutlinedIcon className="flip-rtl" fontSize="small" />
          </ListItemIcon>
          {t("account.logOut")}
        </MenuItem>
      </Menu>
      <Dialog
        open={confirmLogoutOpen}
        onClose={() => setConfirmLogoutOpen(false)}
      >
        <DialogTitle>{t("account.logOutConfirm")}</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmLogoutOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={endSession}
            variant="contained"
            color="error"
            startIcon={<LogoutOutlinedIcon className="flip-rtl" />}
          >
            {t("account.logOut")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
